/**
 * Machine page › Telemetry.
 *
 * The demo's `machineTab('telemetry')` (line 2209):
 *
 * - the open alert's shift-end trend, when the machine has one (L-33's
 *   charging-connector insulation);
 * - the tracked metric's daily mean, with the class limit drawn in;
 * - alarms and abuse events per day, with the alarm codes seen;
 * - S-09's live RMS rotation torque;
 * - the live MQTT feed on the two machines that have one (L-13, T-09),
 *   **replayed** sample by sample (`useLiveReplay`), or — at a shift-end site —
 *   the paragraph saying why there is no live feed.
 *
 * The RMS health summary the demo prints first on this tab is the port's own
 * RMS tab, so it is not repeated here.
 *
 * Domains: `telemetry` always; `telemetry-live` only at a live site and `rms`
 * only for S-09, each fetched by the card that needs it.
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { Pause, Play } from 'lucide-react';
import { unpackTable } from '../../shared/columnar';
import {
  useOemData,
  type DailyMetricRow,
  type EventDailyRow,
  type ShiftDailyRow,
} from '../../shared/useOemData';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import { LineChart } from '../../shared/viz/LineChart';
import { BAND_COLOR } from '../../shared/viz/tokens';
import type { ConditionBand } from '../../schema/machine';
import { TabLoader } from './TabLoader';
import {
  liveFeedFor,
  machineTelemetry,
  s09Torque,
  type LiveFeedView,
} from './MachineScene.transform';
import { useLiveReplay } from './useLiveReplay';

export interface MachineTelemetryTabProps {
  machineId: string;
  band: ConditionBand;
  isLiveSite: boolean;
}

const CARD =
  'rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg';

export function MachineTelemetryTab({ machineId, band, isLiveSite }: MachineTelemetryTabProps) {
  const { data, loading, error } = useOemData('telemetry');

  const view = useMemo(() => {
    if (!data) return null;
    return machineTelemetry(machineId, {
      dailyMetric: unpackTable<DailyMetricRow>(data.daily_metric),
      shiftDaily: unpackTable<ShiftDailyRow>(data.shift_daily),
      eventsDaily: unpackTable<EventDailyRow>(data.events_daily),
      alertSeries: data.alert_series,
    });
  }, [data, machineId]);

  return (
    <TabLoader loading={loading || !view} error={error} what="Telemetry" rows={8}>
      {view ? (
        <div className="@container space-y-4">
          {view.alertTrend ? (
            <ChartCard
              title={view.alertTrend.note}
              subtitle={`${view.alertTrend.metric} · limit ${view.alertTrend.limit} ${view.alertTrend.unit} · shift-end BMS download`}
            >
              <LineChart
                labels={view.alertTrend.points.map((p) => p.label)}
                series={[
                  {
                    values: view.alertTrend.points.map((p) => p.value),
                    color: 'var(--band-vpoor)',
                    area: true,
                    dots: true,
                  },
                ]}
                min={0}
                hline={view.alertTrend.limit}
                hlabel={`${view.alertTrend.limit} ${view.alertTrend.unit} handshake limit`}
                xTitle="Date (2026)"
                yTitle={`Insulation resistance (${view.alertTrend.unit})`}
              />
            </ChartCard>
          ) : null}

          <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
            {view.metric ? (
              <ChartCard title={view.metric.title} subtitle={view.metric.subtitle}>
                <LineChart
                  labels={view.metric.points.map((p) => p.label)}
                  series={[
                    {
                      values: view.metric.points.map((p) => p.value),
                      color: BAND_COLOR[band],
                      area: true,
                      dots: true,
                    },
                  ]}
                  hline={view.metric.limit ?? undefined}
                  hlabel={view.metric.limitLabel}
                  xTitle="Date (2026)"
                  yTitle={view.metric.axis}
                />
              </ChartCard>
            ) : (
              <p className="text-sm text-muted">No tracked metric for {machineId}.</p>
            )}

            <ChartCard
              title="Alarms and abuse events per day"
              subtitle={`Alarm codes seen: ${view.events.codes} · abuse = over-speed, ramming, brake abuse from the FMS`}
            >
              <LineChart
                labels={view.events.labels}
                series={[
                  { values: view.events.alarms, color: 'var(--warn)', name: 'alarms', dots: true },
                  {
                    values: view.events.abuse,
                    color: 'var(--crit)',
                    name: 'abuse events',
                    dots: true,
                  },
                ]}
                min={0}
                xTitle="Date (2026)"
                yTitle="Events per day"
              />
            </ChartCard>
          </div>

          {machineId === 'S-09' ? <S09TorqueCard /> : null}

          {isLiveSite ? (
            <LiveFeedCard machineId={machineId} />
          ) : (
            <section className={CARD}>
              <p className="text-xs uppercase tracking-wider text-muted">Data mode</p>
              <p className="mt-1.5 text-sm text-zinc-700 dark:text-zinc-300">
                {view.mineName} has no underground network coverage on the working levels. Machine
                logs are captured on board and consolidated by Pitram at the end of each shift
                (06:05 / 18:05). Trends are visible a shift late; alarms arrive as counts, not
                timestamps. That is enough for the Diagnostic Agent to see a two-week temperature
                drift — it cannot act mid-shift.
              </p>
            </section>
          )}
        </div>
      ) : null}
    </TabLoader>
  );
}

/** A titled chart panel. */
function ChartCard({
  title,
  subtitle,
  live,
  actions,
  children,
}: {
  title: string;
  subtitle: string;
  live?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={CARD}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          {live ? <LiveChip label={live} /> : null}
          {title}
        </h3>
        {actions}
      </div>
      <p className="mb-2 mt-0.5 text-[11px] text-muted">{subtitle}</p>
      {children}
    </section>
  );
}

function LiveChip({ label }: { label: string }) {
  return (
    <span className="mr-2 inline-flex items-center gap-1.5 align-middle text-[10px] font-medium uppercase tracking-wider text-[var(--crit)]">
      <i aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--crit)]" />
      {label}
    </span>
  );
}

/** Demo line 2213. S-09's live rotation torque, 3-minute samples. */
function S09TorqueCard() {
  const { data, loading, error } = useOemData('rms');
  const torque = useMemo(() => (data ? s09Torque(data.rms_live_s09) : null), [data]);
  const oem = useOemDocIdentity();

  return (
    <TabLoader loading={loading || !torque} error={error} what="The RMS feed" rows={4}>
      {torque ? (
        <ChartCard
          live="Live · RMS"
          title="S-09 · 09 Sep 00:00–04:30 · rotation torque, 3-minute samples"
          subtitle={`${oem.short} RMS over site LTE · torque oscillation growing with hole depth; case-drain 4.9 L/min against 1.9 baseline · ${torque.values.length} drilling samples`}
        >
          <LineChart
            labels={torque.labels}
            series={[
              {
                values: torque.values,
                color: 'var(--crit)',
                area: true,
                name: 'rotation torque kN·m',
              },
            ]}
            min={0}
            dec={1}
            xticks={10}
            width={940}
            height={230}
            xTitle="Time, 09 Sep 2026 (ACST) — drilling samples only"
            yTitle="Rotation torque (kN·m)"
          />
        </ChartCard>
      ) : null}
    </TabLoader>
  );
}

/** Demo line 2213. The live MQTT feed, replayed. Renders nothing without one. */
function LiveFeedCard({ machineId }: { machineId: string }) {
  const { data, loading, error } = useOemData('telemetry-live');
  const feed = useMemo(() => (data ? liveFeedFor(machineId, data.live) : null), [data, machineId]);

  if (!loading && !error && !feed) return null;
  return (
    <TabLoader loading={loading} error={error} what="The live feed" rows={4}>
      {feed ? <LiveFeedChart machineId={machineId} feed={feed} /> : null}
    </TabLoader>
  );
}

function LiveFeedChart({ machineId, feed }: { machineId: string; feed: LiveFeedView }) {
  const total = feed.values.length;
  const { frame, playing, play, pause, showAll } = useLiveReplay(total);

  const shown = useMemo(
    () => feed.values.map((v, i) => (i < frame ? v : null)),
    [feed.values, frame],
  );
  const current = feed.values[Math.max(0, frame - 1)];
  const alarms = feed.alarms.slice(0, frame).filter(Boolean).length;

  return (
    <ChartCard
      live="Live"
      title={`${machineId} · 08 Sep day shift · MQTT feed sampled every 3 minutes`}
      subtitle={`mines/MI/fleet/${machineId}/telemetry · ${feed.caption} · ${total} samples`}
      actions={
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <span className="mono" aria-live="polite">
            {feed.labels[Math.max(0, frame - 1)]} · {current} °C · {alarms} alarm
            {alarms === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={playing ? pause : play}
            aria-label={playing ? 'Pause the replay' : 'Replay the shift'}
            title={playing ? 'Pause the replay' : 'Replay the shift'}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--glass-border)] text-muted transition-colors hover:bg-[var(--glass-surface-strong)]"
          >
            {playing ? (
              <Pause className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
          {frame < total ? (
            <button
              type="button"
              onClick={showAll}
              className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
            >
              Show all
            </button>
          ) : null}
        </div>
      }
    >
      <LineChart
        labels={feed.labels}
        series={[{ values: shown, color: 'var(--crit)', area: true, name: feed.seriesName }]}
        min={feed.min}
        max={feed.max}
        hline={feed.limit}
        hlabel="limit"
        xticks={12}
        width={940}
        height={230}
        xTitle="Time of day, 08 Sep 2026 (AEST)"
        yTitle="Temperature (°C)"
        ariaLabel={`${feed.seriesName} over the 08 Sep day shift`}
      />
    </ChartCard>
  );
}
